import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { isErrorResponse } from 'src/app/models/error-response';

export interface UnexpectedFailure {
  reference: string;
}

/**
 * Tag key shared with the API (CalystaExceptionHandler.ReferenceTag). The reference shown in the
 * error dialog is set as this tag on the Sentry event, so `error_reference:4f2a9c1b` in Sentry's
 * search finds it -- an id prefix would not, Sentry only matches full 32-char event ids.
 */
export const ERROR_REFERENCE_TAG = 'error_reference';

/** Same shape as the API's references: 8 lowercase hex characters. */
export function newErrorReference(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 8);
}

/**
 * Module-level (not class-level) so plain call sites can check `isReported` without injecting
 * the service -- e.g. the ~17 pre-existing `console.error(err)` sites this project's
 * `captureConsoleIntegration` sweeps up. Object identity survives the RxJS pipeline (the
 * interceptor rethrows the exact same err instance), but would NOT survive a Sentry `beforeSend`
 * hook: Sentry's internal `normalize()` step deep-clones `event.extra` before `beforeSend` runs,
 * so comparing against `event.extra.arguments` there would always miss. Gating at the call site,
 * before the object ever reaches Sentry's console integration, sidesteps that entirely.
 */
const reported = new WeakSet<object>();

/**
 * True if `err` was already captured via `reportIfUnexpected` (the interceptor's path). Existing
 * `console.error(err)` sites should skip logging when this is true, so `captureConsoleIntegration`
 * doesn't sweep up a failure the interceptor already reported to Sentry -- e.g.:
 * `if (!isReported(err)) console.error('...', err);`
 * Sites whose error never went through the interceptor (a different HTTP client, a non-HTTP
 * failure) are unaffected -- this is always `false` for them, so they keep logging/capturing
 * exactly as before.
 */
export function isReported(err: unknown): boolean {
  return typeof err === 'object' && err !== null && reported.has(err);
}

/**
 * The single place that decides "expected" vs "unexpected" for an HTTP failure, and the one
 * anti-double-report gate every capture point checks. An error object is tagged the moment it is
 * reported, so if the SAME error instance is later seen by a second capture point (the global
 * ErrorHandler, or notifyHttpError's own toast), that second point can tell "already handled"
 * apart from "never seen" without re-deriving the classification.
 * <para>
 * Expected: HTTP 400 carrying FastEndpoints' validation-failure shape
 * ({ Errors: { GeneralErrors: string[] } }) -- genuine validation, not a defect. Reported nowhere:
 * no Sentry event, existing toast unchanged.
 * </para>
 * <para>
 * Unexpected: everything else -- network failures (status 0), 500s, any other shape. Captured to
 * Sentry and tagged so the caller can show the persistent dialog.
 * </para>
 */
/**
 * Appends " [ref xxxxxxxx]" to the exception text Sentry shows (the last value), so pasting the
 * bare reference in Sentry's search bar finds the event -- free text matches the message, not tag
 * values. Only the event's copy changes, never the thrown error.
 */
export function appendReference(event: Sentry.Event, reference: string): Sentry.Event {
  const shown = event.exception?.values?.at(-1);
  if (shown) {
    shown.value = `${shown.value ?? ''} [ref ${reference}]`.trim();
  }
  return event;
}

@Injectable({ providedIn: 'root' })
export class ErrorReportingService {
  reportIfUnexpected(err: unknown): UnexpectedFailure | null {
    if (this.isExpected(err)) {
      return null;
    }

    if (typeof err === 'object' && err !== null) {
      reported.add(err);
    }

    // Reuse the API's reference when it sent one, so the same 8 characters find both the
    // crystal-web event and the API event; otherwise (status 0, a proxy 502...) mint our own.
    const reference =
      err instanceof HttpErrorResponse && isErrorResponse(err.error) && err.error.ErrorReference
        ? err.error.ErrorReference
        : newErrorReference();

    this.captureUnexpected(err, reference);

    return { reference };
  }

  /**
   * Captures an error that reached Angular's global ErrorHandler (a template binding, an
   * unhandled promise) through Sentry's own handler, tagged with a fresh reference the dialog can
   * show. withScope is synchronous and Sentry's handler captures synchronously, so the tag lands
   * on that event only.
   */
  reportUncaught(error: unknown, sentryHandler: ErrorHandler): string {
    const reference = newErrorReference();
    Sentry.withScope((scope) => {
      scope.setTag(ERROR_REFERENCE_TAG, reference);
      scope.addEventProcessor((event) => appendReference(event, reference));
      sentryHandler.handleError(error);
    });
    return reference;
  }

  wasReported(err: unknown): boolean {
    return isReported(err);
  }

  /**
   * HttpErrorResponse implements Error's shape but doesn't extend Error, so Sentry treats it as a
   * plain object: a generic "Object captured as exception" message, a synthetic stack trace
   * identical for every HTTP failure app-wide, and the real status/URL buried in
   * extra.__serialized__. Wrap it in a real Error with a descriptive message and a status+URL
   * fingerprint so recurring failures on the same endpoint group sensibly instead of all piling
   * into the same generic issue. Query strings are dropped from the URL -- they can carry
   * business-sensitive filter values, per this project's PII rule.
   */
  private captureUnexpected(err: unknown, reference: string): void {
    const tags = { [ERROR_REFERENCE_TAG]: reference };
    if (err instanceof HttpErrorResponse) {
      const urlWithoutQuery = (err.url ?? 'unknown-url').split('?')[0];
      // The reference in the message makes the bare 8 characters findable in Sentry's search bar;
      // grouping is unaffected since the fingerprint below is explicit.
      const message = `HTTP ${err.status} for ${urlWithoutQuery} [ref ${reference}]`;
      const captured = Object.assign(new Error(message), {
        name: 'HttpError',
      });
      Sentry.captureException(captured, {
        fingerprint: ['http-error', String(err.status), urlWithoutQuery],
        tags,
        contexts: {
          response: { status: err.status, url: urlWithoutQuery, reference },
        },
      });
      return;
    }
    Sentry.withScope((scope) => {
      scope.setTags(tags);
      scope.addEventProcessor((event) => appendReference(event, reference));
      Sentry.captureException(err);
    });
  }

  /**
   * True for HTTP 400s carrying FastEndpoints' validation-failure shape -- genuine validation,
   * not a defect. Public so the global ErrorHandler (app.module.ts) can also skip these when an
   * unhandled subscribe() lets the error reach Angular's ErrorHandler untagged (nothing tags an
   * expected error, since reportIfUnexpected never reports it).
   */
  isExpected(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;
    if (err.status !== 400) return false;
    const body = err.error as { Errors?: { GeneralErrors?: unknown } } | null;
    return Array.isArray(body?.Errors?.GeneralErrors);
  }
}
